import { createEmptyMicronutrients } from "../../nutrition/shared/micronutrients";

function getFiniteNumber(value, fallback = 0) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function createEmptyMicros() {
  return createEmptyMicronutrients();
}

function getNutrientMap(nutrientsPer100g) {
  const nutrientMap = {
    calories: 0,
    carbs: 0,
    protein: 0,
    fats: 0,
    fiber: 0,
    ...createEmptyMicros(),
  };

  (Array.isArray(nutrientsPer100g) ? nutrientsPer100g : []).forEach((entry) => {
    const nutrientId = String(entry?.id ?? "").trim();
    if (!nutrientId) {
      return;
    }

    nutrientMap[nutrientId] = getFiniteNumber(entry?.value, 0);
  });

  return nutrientMap;
}

function scaleNutrients(nutrientsPer100g, quantityInGrams, multiplier = 1) {
  const nutrientMap = getNutrientMap(nutrientsPer100g);
  const factor =
    getFiniteNumber(quantityInGrams, 0) > 0
      ? (getFiniteNumber(quantityInGrams, 0) / 100) * getFiniteNumber(multiplier, 1)
      : 0;
  const micros = createEmptyMicros();

  Object.keys(micros).forEach((key) => {
    micros[key] = getFiniteNumber(nutrientMap[key], 0) * factor;
  });

  return {
    calories: getFiniteNumber(nutrientMap.calories, 0) * factor,
    carbs: getFiniteNumber(nutrientMap.carbs, 0) * factor,
    protein: getFiniteNumber(nutrientMap.protein, 0) * factor,
    fats: getFiniteNumber(nutrientMap.fats, 0) * factor,
    fiber: getFiniteNumber(nutrientMap.fiber, 0) * factor,
    micros,
  };
}

function createEmptyTotals() {
  return {
    calories: 0,
    carbs: 0,
    protein: 0,
    fats: 0,
    fiber: 0,
    micros: createEmptyMicros(),
  };
}

function addTotals(baseTotals, nextTotals) {
  const mergedTotals = {
    calories: getFiniteNumber(baseTotals?.calories, 0) + getFiniteNumber(nextTotals?.calories, 0),
    carbs: getFiniteNumber(baseTotals?.carbs, 0) + getFiniteNumber(nextTotals?.carbs, 0),
    protein: getFiniteNumber(baseTotals?.protein, 0) + getFiniteNumber(nextTotals?.protein, 0),
    fats: getFiniteNumber(baseTotals?.fats, 0) + getFiniteNumber(nextTotals?.fats, 0),
    fiber: getFiniteNumber(baseTotals?.fiber, 0) + getFiniteNumber(nextTotals?.fiber, 0),
    micros: createEmptyMicros(),
  };

  Object.keys(mergedTotals.micros).forEach((key) => {
    mergedTotals.micros[key] =
      getFiniteNumber(baseTotals?.micros?.[key], 0) +
      getFiniteNumber(nextTotals?.micros?.[key], 0);
  });

  return mergedTotals;
}

export class NutritionFoodItem {
  constructor(data = {}) {
    Object.assign(this, data);
    this.common_measures = Array.isArray(this.common_measures)
      ? this.common_measures
      : Array.isArray(this.commonMeasures)
        ? this.commonMeasures
        : [];
    this.commonMeasures = this.common_measures;
  }

  get quantityG() {
    return getFiniteNumber(this.quantity_g, 0);
  }

  get totals() {
    return scaleNutrients(this.nutrients_per_100g, this.quantityG);
  }
}

export class NutritionRecipeItem {
  constructor(data = {}) {
    Object.assign(this, data);
  }

  get multiplierValue() {
    const multiplier = getFiniteNumber(this.multiplier, 1);
    return multiplier > 0 ? multiplier : 1;
  }

  get totals() {
    return (Array.isArray(this.items) ? this.items : []).reduce((totals, item) => {
      if (item?.type === "equivalence") {
        return addTotals(totals, new NutritionEquivalenceItem(item).referenceTotals);
      }

      if (item?.type === "food") {
        return addTotals(
          totals,
          scaleNutrients(item?.nutrients_per_100g, item?.quantity_g, this.multiplierValue),
        );
      }

      return totals;
    }, createEmptyTotals());
  }
}

export class NutritionEquivalenceItem {
  constructor(data = {}) {
    Object.assign(this, data);
  }

  get referenceTotals() {
    const firstOption = Array.isArray(this.options) ? this.options[0] : null;

    if (!firstOption) {
      return createEmptyTotals();
    }

    return scaleNutrients(
      firstOption.nutrients_per_100g,
      firstOption.reference_quantity_g ?? firstOption.quantity_g,
    );
  }

  get totals() {
    return this.referenceTotals;
  }
}

export function createNutritionItem(item) {
  if (!item || typeof item !== "object") {
    return item;
  }

  if (item.type === "recipe") {
    return new NutritionRecipeItem(item);
  }

  if (item.type === "equivalence") {
    return new NutritionEquivalenceItem(item);
  }

  return new NutritionFoodItem(item);
}

export {
  addTotals,
  createEmptyMicros,
  createEmptyTotals,
  getFiniteNumber,
  getNutrientMap,
  scaleNutrients,
};
